import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccountBalance,
  Add,
  Delete,
  Download,
  Edit,
  Refresh,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { apiService } from '../services/api';
import { Link as RouterLink } from 'react-router-dom';
import RevenueForecastTab from '../components/financial/RevenueForecastTab';
import PropertyProfitTab from '../components/financial/PropertyProfitTab';

const GREEN = '#2e7d32';
const PIE_COLORS = [
  '#2e7d32',
  '#1565c0',
  '#ef6c00',
  '#6a1b9a',
  '#00838f',
  '#c62828',
  '#558b2f',
  '#4527a0',
  '#f9a825',
  '#ad1457',
  '#37474f',
  '#5d4037',
];

const DISCLAIMER =
  'Financial Dashboard is for informational purposes only and is not accounting, tax, or financial advice. It does not replace professional accounting software or a CPA.';

type TabKey = 'dashboard' | 'forecast' | 'property-profit' | 'expenses' | 'reports' | 'export';
const TAB_KEYS: TabKey[] = ['dashboard', 'forecast', 'property-profit', 'expenses', 'reports', 'export'];

interface DashboardData {
  revenue: { today: number; week: number; month: number; ytd: number };
  expenses: {
    month: number;
    ytd: number;
    monthManual: number;
    ytdManual: number;
    monthPlatformFees: number;
    ytdPlatformFees: number;
  };
  salesTax?: { month: number; ytd: number; note?: string };
  estimatedProfit: { month: number; ytd: number };
  metrics: {
    avgRevenuePerJob: number;
    avgRevenuePerCustomer: number;
    avgRevenuePerDayWorked: number;
    completedJobs: number;
    activeCustomers: number;
    daysWorkedYtd: number;
  };
  disclaimer?: string;
}

interface ExpenseRow {
  _id: string;
  expenseDate: string;
  category: string;
  vendor?: string;
  amount: number;
  notes?: string;
}

interface ReportsData {
  period: string;
  profitAndLoss: {
    revenue: number;
    expenses: number;
    manualExpenses: number;
    platformFees: number;
    salesTaxAmount?: number;
    estimatedProfit: number;
  };
  expensesByCategory: { category: string; amount: number }[];
  revenueTrend: { month: string; amount: number }[];
  topCustomers: { customerId: string; name: string; revenue: number }[];
  topZipCodes: { zipCode: string; revenue: number }[];
}

const money = (n: number | undefined) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatDateInput = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
};

const todayInput = () => new Date().toISOString().slice(0, 10);

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}> = ({ label, value, sub, accent = GREEN }) => (
  <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
    <CardContent sx={{ py: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, color: accent, letterSpacing: '-0.02em' }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const ProviderFinancialDashboardPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [expenses, setExpenses] = useState<ExpenseRow[]>([]);
  const [reportPeriod, setReportPeriod] = useState<'month' | 'quarter' | 'year'>('month');
  const [reports, setReports] = useState<ReportsData | null>(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [exportPeriod, setExportPeriod] = useState<'month' | 'quarter' | 'year'>('year');
  const [exporting, setExporting] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseRow | null>(null);
  const [form, setForm] = useState({
    expenseDate: todayInput(),
    category: '',
    vendor: '',
    amount: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const loadDashboard = useCallback(async () => {
    const res = await apiService.get<DashboardData>('/provider-financial/dashboard');
    if (res.success && res.data) setDashboard(res.data);
    else throw new Error(res.error || 'Failed to load dashboard');
  }, []);

  const loadCategories = useCallback(async () => {
    const res = await apiService.get<string[]>('/provider-financial/categories');
    if (res.success && res.data) setCategories(res.data);
  }, []);

  const loadExpenses = useCallback(async () => {
    const res = await apiService.get<ExpenseRow[]>('/provider-financial/expenses', { limit: 100 });
    if (res.success && res.data) setExpenses(res.data);
    else throw new Error(res.error || 'Failed to load expenses');
  }, []);

  const loadReports = useCallback(async (period: string) => {
    setReportsLoading(true);
    try {
      const res = await apiService.get<ReportsData>('/provider-financial/reports', { period });
      if (res.success && res.data) setReports(res.data);
      else throw new Error(res.error || 'Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await Promise.all([loadDashboard(), loadCategories(), loadExpenses()]);
    } catch (err: any) {
      setError(err.message || 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  }, [loadDashboard, loadCategories, loadExpenses]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (tab === 'reports') {
      loadReports(reportPeriod).catch((err: any) => setError(err.message || 'Failed to load reports'));
    }
  }, [tab, reportPeriod, loadReports]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      expenseDate: todayInput(),
      category: categories[0] || 'Fuel',
      vendor: '',
      amount: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (row: ExpenseRow) => {
    setEditing(row);
    setForm({
      expenseDate: formatDateInput(row.expenseDate),
      category: row.category,
      vendor: row.vendor || '',
      amount: String(row.amount),
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const saveExpense = async () => {
    try {
      setSaving(true);
      setError(null);
      const payload = {
        expenseDate: form.expenseDate,
        category: form.category,
        vendor: form.vendor.trim() || undefined,
        amount: Number(form.amount),
        notes: form.notes.trim() || undefined,
      };
      if (!payload.expenseDate || !payload.category || !payload.amount || payload.amount <= 0) {
        setError('Date, category, and a valid amount are required');
        return;
      }
      if (editing) {
        await apiService.put(`/provider-financial/expenses/${editing._id}`, payload);
        setSuccess('Expense updated');
      } else {
        await apiService.post('/provider-financial/expenses', payload);
        setSuccess('Expense saved');
      }
      setDialogOpen(false);
      await Promise.all([loadExpenses(), loadDashboard()]);
    } catch (err: any) {
      setError(err.message || 'Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiService.delete(`/provider-financial/expenses/${deleteId}`);
      setDeleteId(null);
      setSuccess('Expense deleted');
      await Promise.all([loadExpenses(), loadDashboard()]);
    } catch (err: any) {
      setError(err.message || 'Failed to delete expense');
    }
  };

  const downloadExport = async (type: string) => {
    try {
      setExporting(type);
      setError(null);
      const base = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const apiBase = base.includes('/api') ? base.replace(/\/$/, '') : `${base.replace(/\/$/, '')}/api`;
      const url = `${apiBase}/provider-financial/export/${type}?period=${exportPeriod}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Export failed');
      }
      const disposition = response.headers.get('Content-Disposition');
      let filename = `lawnhub-${type}.csv`;
      const match = disposition?.match(/filename="?([^"]+)"?/);
      if (match) filename = match[1];
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(link.href);
      document.body.removeChild(link);
      setSuccess(`${filename} downloaded`);
    } catch (err: any) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(null);
    }
  };

  const tabIndex = Math.max(0, TAB_KEYS.indexOf(tab));

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance sx={{ color: GREEN }} />
            Financial Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            See if you&apos;re making money — revenue from LawnHub, expenses you enter.
          </Typography>
        </Box>
        <IconButton onClick={refreshAll} aria-label="Refresh">
          <Refresh />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, i) => setTab(TAB_KEYS[i])}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            '& .Mui-selected': { color: `${GREEN} !important` },
            '& .MuiTabs-indicator': { backgroundColor: GREEN },
          }}
        >
          <Tab label="Dashboard" />
          <Tab label="Forecast" />
          <Tab label="Property Profit" />
          <Tab label="Expenses" />
          <Tab label="Reports" />
          <Tab label="Export" />
        </Tabs>
      </Paper>

      {tab === 'dashboard' && dashboard && (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Revenue
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <StatCard label="Today" value={money(dashboard.revenue.today)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="This Week" value={money(dashboard.revenue.week)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="This Month" value={money(dashboard.revenue.month)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard label="Year to Date" value={money(dashboard.revenue.ytd)} />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Sales Tax Collected (you remit)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="This Month"
                  value={money(dashboard.salesTax?.month)}
                  sub="Included in your payout"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Year to Date"
                  value={money(dashboard.salesTax?.ytd)}
                  sub="Not a LawnHub fee"
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Expenses &amp; Profit
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Expenses (Month)"
                  value={money(dashboard.expenses.month)}
                  sub={`Entered ${money(dashboard.expenses.monthManual)} + LawnHub fees ${money(dashboard.expenses.monthPlatformFees)}`}
                  accent="#c62828"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Expenses (YTD)"
                  value={money(dashboard.expenses.ytd)}
                  sub={`Entered ${money(dashboard.expenses.ytdManual)} + LawnHub fees ${money(dashboard.expenses.ytdPlatformFees)}`}
                  accent="#c62828"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Est. Profit (Month)"
                  value={money(dashboard.estimatedProfit.month)}
                  accent={dashboard.estimatedProfit.month >= 0 ? GREEN : '#c62828'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <StatCard
                  label="Est. Profit (YTD)"
                  value={money(dashboard.estimatedProfit.ytd)}
                  accent={dashboard.estimatedProfit.ytd >= 0 ? GREEN : '#c62828'}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              Metrics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4} md={4}>
                <StatCard label="Avg / Job" value={money(dashboard.metrics.avgRevenuePerJob)} />
              </Grid>
              <Grid item xs={6} sm={4} md={4}>
                <StatCard label="Avg / Customer" value={money(dashboard.metrics.avgRevenuePerCustomer)} />
              </Grid>
              <Grid item xs={6} sm={4} md={4}>
                <StatCard label="Avg / Day Worked" value={money(dashboard.metrics.avgRevenuePerDayWorked)} />
              </Grid>
              <Grid item xs={6} sm={6} md={6}>
                <StatCard label="Completed Jobs" value={String(dashboard.metrics.completedJobs)} />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <StatCard label="Active Customers" value={String(dashboard.metrics.activeCustomers)} sub="Scheduled or accepted" />
              </Grid>
            </Grid>
          </Box>

          <Alert severity="info">{dashboard.disclaimer || DISCLAIMER}</Alert>
          <Typography variant="body2" color="text.secondary">
            Payment and payout status live in{' '}
            <Button component={RouterLink} to="/provider/earnings" size="small" sx={{ textTransform: 'none', p: 0, minWidth: 0 }}>
              My Earnings
            </Button>
            .
          </Typography>
        </Stack>
      )}

      {tab === 'forecast' && <RevenueForecastTab />}

      {tab === 'property-profit' && <PropertyProfitTab />}

      {tab === 'expenses' && (
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              Enter business costs only. Revenue is tracked automatically. LawnHub fees are added for you.
            </Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#1b5e20' } }}>
              Add Expense
            </Button>
          </Box>

          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Vendor</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No expenses yet. Add your first one in under 15 seconds.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((row) => (
                    <TableRow key={row._id} hover>
                      <TableCell>{formatDateInput(row.expenseDate)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.category} variant="outlined" />
                      </TableCell>
                      <TableCell>{row.vendor || '—'}</TableCell>
                      <TableCell align="right">{money(row.amount)}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {row.notes || '—'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(row)} aria-label="Edit">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setDeleteId(row._id)} aria-label="Delete">
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}

      {tab === 'reports' && (
        <Stack spacing={3}>
          <FormControl size="small" sx={{ maxWidth: 220 }}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              value={reportPeriod}
              onChange={(e) => setReportPeriod(e.target.value as 'month' | 'quarter' | 'year')}
            >
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">This Quarter</MenuItem>
              <MenuItem value="year">Year to Date</MenuItem>
            </Select>
          </FormControl>

          {reportsLoading || !reports ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box>
                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Profit &amp; Loss — {reports.period}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={4} lg={2}>
                    <StatCard label="Revenue" value={money(reports.profitAndLoss.revenue)} />
                  </Grid>
                  <Grid item xs={6} md={4} lg={2}>
                    <StatCard
                      label="Sales Tax Collected"
                      value={money(reports.profitAndLoss.salesTaxAmount)}
                      sub="You remit"
                    />
                  </Grid>
                  <Grid item xs={6} md={4} lg={2}>
                    <StatCard label="Expenses" value={money(reports.profitAndLoss.expenses)} accent="#c62828" />
                  </Grid>
                  <Grid item xs={6} md={4} lg={2}>
                    <StatCard label="LawnHub Fees" value={money(reports.profitAndLoss.platformFees)} accent="#c62828" />
                  </Grid>
                  <Grid item xs={6} md={4} lg={2}>
                    <StatCard
                      label="Estimated Profit"
                      value={money(reports.profitAndLoss.estimatedProfit)}
                      accent={reports.profitAndLoss.estimatedProfit >= 0 ? GREEN : '#c62828'}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: 360 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Expenses by Category
                    </Typography>
                    {reports.expensesByCategory.length === 0 ? (
                      <Typography color="text.secondary" sx={{ mt: 8, textAlign: 'center' }}>
                        No expenses in this period
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height="90%">
                        <PieChart>
                          <Pie
                            data={reports.expensesByCategory}
                            dataKey="amount"
                            nameKey="category"
                            cx="50%"
                            cy="45%"
                            outerRadius={90}
                            label={false}
                          >
                            {reports.expensesByCategory.map((_, i) => (
                              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => money(Number(v ?? 0))} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', height: 360 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                      Revenue Trend (12 months)
                    </Typography>
                    <ResponsiveContainer width="100%" height="90%">
                      <LineChart data={reports.revenueTrend}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => money(Number(v ?? 0))} />
                        <Line type="monotone" dataKey="amount" stroke={GREEN} strokeWidth={2} dot={false} name="Revenue" />
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography fontWeight={600}>Top Customers</Typography>
                    </Box>
                    <Table size="small">
                      <TableBody>
                        {reports.topCustomers.length === 0 ? (
                          <TableRow>
                            <TableCell>No revenue in this period</TableCell>
                          </TableRow>
                        ) : (
                          reports.topCustomers.map((c, i) => (
                            <TableRow key={c.customerId}>
                              <TableCell width={40}>{i + 1}</TableCell>
                              <TableCell>{c.name}</TableCell>
                              <TableCell align="right">{money(c.revenue)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                      <Typography fontWeight={600}>Highest Revenue ZIP Codes</Typography>
                    </Box>
                    <Table size="small">
                      <TableBody>
                        {reports.topZipCodes.length === 0 ? (
                          <TableRow>
                            <TableCell>No revenue in this period</TableCell>
                          </TableRow>
                        ) : (
                          reports.topZipCodes.map((z, i) => (
                            <TableRow key={z.zipCode}>
                              <TableCell width={40}>{i + 1}</TableCell>
                              <TableCell>{z.zipCode}</TableCell>
                              <TableCell align="right">{money(z.revenue)}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}
        </Stack>
      )}

      {tab === 'export' && (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Download CSV files for your accountant or to import into QuickBooks and similar tools. LawnHub does not connect directly to QuickBooks.
          </Typography>
          <FormControl size="small" sx={{ maxWidth: 220 }}>
            <InputLabel>Period</InputLabel>
            <Select
              label="Period"
              value={exportPeriod}
              onChange={(e) => setExportPeriod(e.target.value as 'month' | 'quarter' | 'year')}
            >
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="quarter">This Quarter</MenuItem>
              <MenuItem value="year">Year to Date</MenuItem>
            </Select>
          </FormControl>
          <Grid container spacing={2}>
            {[
              { type: 'revenue', label: 'Revenue', desc: 'Jobs, service price, your earnings, LawnHub fee' },
              { type: 'expenses', label: 'Expenses', desc: 'Your entries plus LawnHub fees by month' },
              { type: 'profit-loss', label: 'Profit & Loss', desc: 'Simple summary of revenue, expenses, profit' },
              { type: 'customers', label: 'Customer Revenue', desc: 'Revenue ranked by customer' },
              { type: 'monthly-summary', label: 'Monthly Summary', desc: 'Month-by-month revenue and profit' },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.type}>
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%' }}>
                  <CardContent>
                    <Typography fontWeight={600} gutterBottom>
                      {item.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                      {item.desc}
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={exporting === item.type ? <CircularProgress size={16} /> : <Download />}
                      disabled={!!exporting}
                      onClick={() => downloadExport(item.type)}
                      sx={{ borderColor: GREEN, color: GREEN }}
                    >
                      Download CSV
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Stack>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Date"
              type="date"
              value={form.expenseDate}
              onChange={(e) => setForm((f) => ({ ...f, expenseDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              >
                {categories.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Vendor (optional)"
              value={form.vendor}
              onChange={(e) => setForm((f) => ({ ...f, vendor: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Amount"
              type="number"
              value={form.amount}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
              inputProps={{ min: 0.01, step: 0.01 }}
              fullWidth
              required
            />
            <TextField
              label="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveExpense}
            disabled={saving}
            sx={{ bgcolor: GREEN, '&:hover': { bgcolor: '#1b5e20' } }}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete expense?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This removes the expense from your reports. You can always add it again later.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ProviderFinancialDashboardPage;
